import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, parseISO, startOfMonth, getDay, getDaysInMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface CollectsCalendarProps {
  month: string;
  onMonthChange: (month: string) => void;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  markedDates: Set<string>;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function CollectsCalendar({
  month,
  onMonthChange,
  selectedDate,
  onSelectDate,
  markedDates,
}: CollectsCalendarProps) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const currentMonth = format(new Date(), 'yyyy-MM');

  const cells = useMemo(() => {
    const monthStart = parseISO(`${month}-01`);
    const startWeekday = getDay(startOfMonth(monthStart));
    const daysInMonth = getDaysInMonth(monthStart);
    const result: Array<{ date: string; day: number } | null> = Array.from(
      { length: startWeekday },
      () => null,
    );
    for (let d = 1; d <= daysInMonth; d++) {
      result.push({ date: `${month}-${String(d).padStart(2, '0')}`, day: d });
    }
    while (result.length % 7 !== 0) result.push(null);
    return result;
  }, [month]);

  function prevMonth() {
    const [y, m] = month.split('-').map(Number);
    onMonthChange(format(new Date(y ?? 0, (m ?? 1) - 2, 1), 'yyyy-MM'));
  }

  function nextMonth() {
    const [y, m] = month.split('-').map(Number);
    onMonthChange(format(new Date(y ?? 0, m ?? 1, 1), 'yyyy-MM'));
  }

  const monthLabel = format(parseISO(`${month}-01`), 'MMMM yyyy', { locale: ptBR });

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4" role="region" aria-label="Calendário de pontos">
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={prevMonth}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold capitalize text-slate-900">{monthLabel}</span>
        <button
          onClick={nextMonth}
          disabled={month >= currentMonth}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-30"
          aria-label="Próximo mês"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 text-center">
        {WEEKDAYS.map((d) => (
          <span key={d} className="text-[10px] font-medium text-slate-400">{d}</span>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          if (!cell) {
            return <div key={`e-${i}`} className="h-10" />;
          }
          const { date, day } = cell;
          const isSelected = date === selectedDate;
          const isToday = date === today;
          const hasCollect = markedDates.has(date);

          return (
            <button
              key={date}
              onClick={() => onSelectDate(date)}
              aria-label={format(parseISO(`${date}T12:00:00`), "d 'de' MMMM", { locale: ptBR })}
              aria-pressed={isSelected}
              className="flex flex-col items-center py-0.5"
            >
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                  isSelected && 'bg-slate-900 text-white',
                  !isSelected && isToday && 'ring-1 ring-slate-400 text-slate-900',
                  !isSelected && !isToday && 'text-slate-700 hover:bg-slate-100',
                )}
              >
                {day}
              </span>
              <span
                className={cn('h-1 w-1 rounded-full', hasCollect ? 'bg-slate-400' : 'bg-transparent')}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
